import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/** Proactive re-negotiate cadence (server token TTL is 60 minutes). */
const NEGOTIATE_REFRESH_MS = 50 * 60 * 1000;
const START_RETRY_MS = 3000;
const DEFAULT_GROUP = 'admin_orders';

function ts() {
  return new Date().toISOString();
}

function parseMessagePayload(raw) {
  if (raw == null) {
    throw new Error('empty message');
  }
  if (typeof raw === 'string') {
    return JSON.parse(raw);
  }
  if (raw instanceof ArrayBuffer) {
    return JSON.parse(new TextDecoder().decode(raw));
  }
  if (ArrayBuffer.isView(raw)) {
    return JSON.parse(new TextDecoder().decode(raw));
  }
  if (typeof raw === 'object') {
    if (raw.kind === 'groupData' || raw.kind === 'serverData') {
      return parseMessagePayload(raw.data);
    }
    if (raw.order_id || raw.message) {
      return raw;
    }
    if (raw.data != null) {
      return parseMessagePayload(raw.data);
    }
  }
  throw new Error(`unrecognized payload type: ${typeof raw}`);
}

/**
 * Azure Web PubSub listener for admin order events (placed + cancelled).
 *
 * One WebPubSubClient for the lifetime of this effect. Fresh negotiate URLs come
 * from getClientAccessUrl so SDK autoReconnect does not reuse a dead token.
 * Cleanup runs only on unmount / enabled=false — not when the parent re-renders.
 */
export default function useWebPubSubOrders({
  onNewOrder,
  onOrderCancelled,
  enabled = true,
} = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const onNewOrderRef = useRef(onNewOrder);
  const onOrderCancelledRef = useRef(onOrderCancelled);
  onNewOrderRef.current = onNewOrder;
  onOrderCancelledRef.current = onOrderCancelled;

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      return undefined;
    }

    let cancelled = false;
    let client = null;
    let refreshTimer = null;
    let retryTimer = null;
    let joinedGroup = DEFAULT_GROUP;
    let connectedAt = null;
    let liveConnected = false;

    const log = (msg, extra) => {
      if (extra !== undefined) {
        console.log(`[${ts()}] ${msg}`, extra);
      } else {
        console.log(`[${ts()}] ${msg}`);
      }
    };

    const handleIncoming = (label, raw) => {
      log(`📩 Web PubSub raw (${label}):`, raw);
      try {
        const data = parseMessagePayload(raw);
        log('📩 Web PubSub parsed:', data);
        if (!(data?.order_id || data?.message || data?.event)) {
          console.warn(
            `[${ts()}] ⚠️ Web PubSub message missing order_id/message/event:`,
            data
          );
          return;
        }

        const eventType = String(data?.event || '').toLowerCase();
        const messageText = String(data?.message || '').toLowerCase();
        const isCancel =
          eventType === 'order_cancelled' ||
          messageText === 'order cancelled' ||
          String(data?.status || '').toLowerCase() === 'cancelled';

        if (isCancel) {
          onOrderCancelledRef.current?.(data);
          return;
        }

        // Default / order_placed — existing new-order path (unchanged behavior).
        onNewOrderRef.current?.(data);
      } catch (err) {
        console.warn(
          `[${ts()}] ⚠️ Web PubSub parse failed:`,
          err?.message || err,
          raw
        );
      }
    };

    const negotiateUrl = async () => {
      const apiBase = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('authtoken');
      if (!apiBase || !token) {
        throw new Error('Missing API URL or auth token for Web PubSub negotiate');
      }
      try {
        const { data } = await axios.get(`${apiBase}webpubsub/negotiate/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!data?.url) {
          throw new Error(data?.error || 'Negotiate returned no URL');
        }
        joinedGroup = data.group || DEFAULT_GROUP;
        let host = '(unparseable)';
        try {
          host = new URL(data.url).host;
        } catch {
          /* ignore */
        }
        log('✅ Web PubSub negotiated', {
          host,
          hub: data.hub,
          group: joinedGroup,
          tokenTtlMinutes: 60,
          protocol: data.protocol,
        });
        return data.url;
      } catch (err) {
        const apiErr = err?.response?.data?.error || err?.message || err;
        const protocol = err?.response?.data?.protocol;
        console.error(`[${ts()}] ❌ Web PubSub negotiate failed:`, apiErr, protocol || '');
        throw new Error(
          typeof apiErr === 'string' ? apiErr : 'Web PubSub negotiate failed'
        );
      }
    };

    const joinGroup = async () => {
      if (!client || cancelled) {
        return;
      }
      try {
        const result = await client.joinGroup(joinedGroup);
        log(`✅ Web PubSub joined group "${joinedGroup}"`, result);
      } catch (joinErr) {
        console.warn(
          `[${ts()}] ⚠️ Web PubSub joinGroup("${joinedGroup}") failed:`,
          joinErr
        );
      }
    };

    const clearRefreshTimer = () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
    };

    const scheduleTokenRefresh = () => {
      clearRefreshTimer();
      refreshTimer = setTimeout(() => {
        if (cancelled) {
          return;
        }
        log('🔄 Web PubSub proactive token refresh (50m) — restarting client');
        restartClient('token-refresh');
      }, NEGOTIATE_REFRESH_MS);
    };

    const stopClient = async () => {
      clearRefreshTimer();
      const toStop = client;
      client = null;
      liveConnected = false;
      connectedAt = null;
      if (!toStop) {
        return;
      }
      try {
        await toStop.stop();
      } catch (err) {
        console.warn(`[${ts()}] ⚠️ Web PubSub stop error:`, err);
      }
    };

    const attachHandlers = (instance, reason) => {
      instance.on('connected', async () => {
        if (cancelled || client !== instance) {
          return;
        }
        connectedAt = Date.now();
        liveConnected = true;
        setIsConnected(true);
        setError(null);
        log('✅ Azure Web PubSub connected (opened)', {
          group: joinedGroup,
          reason,
        });
        await joinGroup();
        scheduleTokenRefresh();
      });

      instance.on('disconnected', (e) => {
        if (client !== instance && client != null) {
          return;
        }
        const connectedForMs = connectedAt ? Date.now() - connectedAt : null;
        connectedAt = null;
        liveConnected = false;
        setIsConnected(false);
        log('🔌 Azure Web PubSub disconnected (closed)', {
          reason: e?.message || e?.reason || String(e ?? ''),
          code: e?.code ?? e?.statusCode,
          connectedForMs,
          cancelled,
          willAutoReconnect: !cancelled,
        });
      });

      instance.on('stopped', () => {
        log('🛑 Azure Web PubSub stopped', { cancelled });
      });

      instance.on('rejoin-group-failed', (e) => {
        if (client !== instance) {
          return;
        }
        console.warn(`[${ts()}] ⚠️ Web PubSub rejoin-group-failed:`, e);
        joinGroup();
      });

      instance.on('group-message', (e) => {
        if (cancelled || client !== instance) {
          return;
        }
        handleIncoming('group-message', e?.message?.data ?? e?.message ?? e);
      });

      instance.on('server-message', (e) => {
        if (cancelled || client !== instance) {
          return;
        }
        handleIncoming('server-message', e?.message?.data ?? e?.message ?? e);
      });
    };

    const startClient = async (reason) => {
      if (cancelled) {
        return;
      }
      log(`🔌 Web PubSub startClient (${reason})`);
      try {
        const { WebPubSubClient } = await import('@azure/web-pubsub-client');
        const instance = new WebPubSubClient(
          { getClientAccessUrl: negotiateUrl },
          {
            autoReconnect: true,
            autoRejoinGroups: true,
          }
        );
        client = instance;
        attachHandlers(instance, reason);
        await instance.start();
        if (cancelled || client !== instance) {
          try {
            await instance.stop();
          } catch {
            /* ignore */
          }
          return;
        }
        log(`✅ Web PubSub client.start() resolved (${reason})`);
        await joinGroup();
      } catch (err) {
        const detail = err?.response?.data || err?.message || err;
        console.warn(`[${ts()}] ⚠️ Web PubSub start failed:`, detail);
        setError(err?.message || 'Web PubSub connect failed');
        setIsConnected(false);
        liveConnected = false;
        if (!cancelled) {
          if (retryTimer) {
            clearTimeout(retryTimer);
          }
          retryTimer = setTimeout(() => {
            if (!cancelled && !liveConnected && !client) {
              startClient('retry-after-error');
            }
          }, START_RETRY_MS);
        }
      }
    };

    const restartClient = async (reason) => {
      if (cancelled) {
        return;
      }
      await stopClient();
      if (!cancelled) {
        await startClient(reason);
      }
    };

    startClient('initial');

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      clearRefreshTimer();
      log('🧹 Web PubSub hook cleanup (unmount / disabled)');
      Promise.resolve(stopClient()).catch(() => {});
      setIsConnected(false);
    };
    // onNewOrder via ref — must NOT be a dependency (would churn the socket).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { isConnected, error };
}
